import { CheckCircle, Info, Warning } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { useTranslations } from 'context/TranslationContext';

import {
  resultsSummaryStyle,
  resultsSummaryIconStyle,
  resultsSummaryAutoApproveStyle,
  resultsSummaryManualStyle,
  resultsSummaryFallbackStyle
} from 'styles/pages/ApprovalRuleSimulatorStyles';

import {
  SimulationOutcome,
  SimulationSummary
} from '../../../../utils/interfaces/ApprovalSimulator';

export interface ResultsSummaryProps {
  summary: SimulationSummary;
}

export const ResultsSummary = ({ summary }: ResultsSummaryProps) => {
  const { translate } = useTranslations();

  const getSummaryOutcomeConfig = () => {
    switch (summary.outcome) {
      case SimulationOutcome.AutoApprove:
        return {
          translationKey: 'approvalSimulator.summary.autoApproveOnly',
          styleConfig: {
            ...resultsSummaryStyle,
            ...resultsSummaryAutoApproveStyle
          },
          iconStyleConfig: {
            ...resultsSummaryIconStyle,
            color: '#2E7D32'
          },
          IconComponent: CheckCircle
        };
      case SimulationOutcome.ManualApproval:
        return {
          translationKey: 'approvalSimulator.summary.manualApprovalRequired',
          translationParams: {
            count: summary.gateCount?.toString() || '0'
          },
          styleConfig: { ...resultsSummaryStyle, ...resultsSummaryManualStyle },
          iconStyleConfig: {
            ...resultsSummaryIconStyle,
            color: '#1976D2'
          },
          IconComponent: Info
        };
      case SimulationOutcome.Fallback:
        return {
          translationKey: 'approvalSimulator.summary.fallbackApplied',
          styleConfig: {
            ...resultsSummaryStyle,
            ...resultsSummaryFallbackStyle
          },
          iconStyleConfig: {
            ...resultsSummaryIconStyle,
            color: '#ed6c02'
          },
          IconComponent: Warning
        };
      default:
        return {
          translationKey: '',
          styleConfig: resultsSummaryStyle,
          iconStyleConfig: resultsSummaryIconStyle,
          IconComponent: null
        };
    }
  };

  const config = getSummaryOutcomeConfig();

  if (!config.IconComponent || !config.translationKey) {
    return null;
  }

  const {
    IconComponent,
    styleConfig,
    iconStyleConfig,
    translationKey,
    translationParams
  } = config;

  return (
    <Box sx={styleConfig}>
      <IconComponent sx={iconStyleConfig} />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontSize: 'inherit' }}>
          {translate(translationKey, translationParams)}
        </Typography>

        {summary.autoApproveOverridden && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5
            }}
          >
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: 'inherit'
              }}
            >
              {translate('approvalSimulator.summary.autoApproveDiscarded')}
            </Typography>

            <Typography sx={{ fontSize: 'inherit' }}>
              {translate('approvalSimulator.summary.autoApproveDiscardedText')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
